import {
  CancelScheduleSendOutlined as CancelScheduleSendOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
  EditNoteOutlined as EditNoteOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  ReplayOutlined as ReplayOutlinedIcon,
  UndoOutlined as UndoOutlinedIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ModulePaymentActionModal from "components/Modals/ModulePaymentActionModal";
import PendingModulePaymentDialog from "components/Payments/PendingModulePaymentDialog";
import { canChangePendingPayment } from "utils/pendingPaymentChange";
import UploadFile from "components/Input/UploadFile";
import { P } from "const/permissions";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import PaymentReceiptChips from "components/Payments/PaymentReceiptChips";
import { hasPermission } from "utils/permissionUtils";
import { formatAudCompact } from "../Admissions/AddAdmissions/applicationFlow/termPaymentUtils";
import {
  addCadPaymentTransaction,
  resubmitModulePayment,
  useCadPaymentSummary,
  useCadPaymentTransactions,
} from "./cadModulePaymentApi";
import { netVerifiedFromTransactions } from "./modulePaymentsRowUtils";

const PAYMENT_METHOD_OPTIONS = [
  { label: "Cash", value: "CASH" },
  { label: "Bank Transfer", value: "BANK_TRANSFER" },
  { label: "Cheque", value: "CHEQUE" },
  { label: "Credit Card", value: "CREDIT_CARD" },
  { label: "Electronic Transfer", value: "ELECTRONIC_TRANSFER" },
  { label: "UPI", value: "UPI" },
  { label: "PayPal", value: "PAYPAL" },
];

const STATUS_CHIP = {
  PENDING_VERIFICATION: { label: "Pending", color: "warning" },
  VERIFIED: { label: "Verified", color: "success" },
  REJECTED: { label: "Rejected", color: "error" },
};

/**
 * autoVerify defaults to whether this user may actually verify. It used to default true for
 * everyone, and the checkbox that controls it only renders for a verifier — so a submitter who
 * cannot verify was silently sending an intent the server was always going to ignore.
 */
const createEmptyDraft = (canSelfVerify = false) => ({
  amount: "",
  date: format(new Date(), "yyyy-MM-dd"),
  reference: "",
  notes: "",
  method: "",
  autoVerify: canSelfVerify,
});

const getApiError = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

/** Payments modal for linked module CAD rows — uses module_payment_transactions (same as RPL/Visa tabs). */
export default function LinkedModulePaymentsModal({
  open,
  onClose,
  cadId,
  moduleName = "",
  agreedAmount = 0,
  /** The row's discount, so Due reads the same here as on the row that opened this. */
  discountAmount = 0,
  onPaymentsChanged,
  /**
   * A custom ("Others") entry, whose payments go through routes that accept
   * accounts:other-entry:manage. Without this an admin holding only that key 403s on every call
   * here — which is the whole flow the key exists for.
   */
  isOtherEntry = false,
}) {
  const queryClient = useQueryClient();
  const {
    data: payments = [],
    isLoading: paymentsLoading,
    refetch: refetchTransactions,
  } = useCadPaymentTransactions(cadId, open, isOtherEntry);
  const { data: summary = {}, refetch: refetchSummary } = useCadPaymentSummary(
    cadId,
    open,
    isOtherEntry
  );

  useEffect(() => {
    if (open && cadId) {
      refetchTransactions();
      refetchSummary();
    }
  }, [open, cadId, refetchTransactions, refetchSummary]);
  const canSelfVerify = hasPermission(P.ACCOUNTS.PAYMENT_VERIFY);
  const [draft, setDraft] = useState(() => createEmptyDraft(canSelfVerify));
  const [upload, setUpload] = useState({ attachments: [] });
  const [actionState, setActionState] = useState({ open: false, action: null, payment: null });
  const [pendingState, setPendingState] = useState({ open: false, mode: null, payment: null });
  const canModify = hasPermission(P.ACCOUNTS.PAYMENT_MODIFY);
  const canRefund = hasPermission(P.ACCOUNTS.PAYMENT_REFUND);
  const canDelete = hasPermission(P.ACCOUNTS.PAYMENT_DELETE);

  const agreed = Number(summary.agreed ?? agreedAmount) || 0;
  const discount = Number(summary.discount ?? discountAmount) || 0;
  const totalPaid = Number(summary.paid) || netVerifiedFromTransactions(payments);
  // An honest 0 from the server is an answer, so the local fallback is only for a summary that has
  // not arrived yet. It prices due the way the account row does, discount included.
  const balanceDue = Number.isFinite(Number(summary.due))
    ? Number(summary.due)
    : Math.max(agreed - discount - totalPaid, 0);
  const refundableAvailable = useMemo(() => {
    const net = netVerifiedFromTransactions(payments);
    const pendingRefunds = payments
      .filter((p) => p.status === "PENDING_VERIFICATION" && p.entryType === "REFUND")
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    return Math.max(0, net - pendingRefunds);
  }, [payments]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["cad-payment-transactions", cadId] });
    queryClient.invalidateQueries({ queryKey: ["cad-payment-summary", cadId] });
    onPaymentsChanged?.();
  };

  const addMutation = useMutation({
    mutationFn: () =>
      addCadPaymentTransaction(
        cadId,
        {
          amount: draft.amount,
          date: draft.date,
          reference: draft.reference?.trim() || null,
          notes: draft.notes?.trim() || null,
          method: draft.method || null,
          receiptDocuments: (upload.attachments || []).filter(Boolean),
          autoVerify: draft.autoVerify,
        },
        isOtherEntry
      ),
    onSuccess: (response) => {
      // The server decides: autoVerify is honoured only for a submitter who may verify, so an admin
      // who sends it still gets PENDING. Reading the request back said "recorded and verified" over
      // a payment sitting in the verification queue.
      const verified = response?.data?.data?.status === "VERIFIED";
      toast.success(
        verified ? "Payment recorded and verified" : "Payment submitted for verification"
      );
      setDraft(createEmptyDraft(canSelfVerify));
      setUpload({ attachments: [] });
      invalidate();
    },
    onError: (err) => toast.error(getApiError(err, "Failed to submit payment")),
  });

  const submitPayment = () => {
    const amount = Number(draft.amount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }
    if (!draft.date) {
      toast.error("Select the payment date");
      return;
    }
    if (!draft.method) {
      toast.error("Select a payment method");
      return;
    }
    addMutation.mutate();
  };

  const handleClose = () => {
    if (addMutation.isLoading) return;
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Payments — {moduleName}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Box className={`grid gap-3 ${discount > 0 ? "grid-cols-4" : "grid-cols-3"}`}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Agreed
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {formatAudCompact(agreed)}
                </Typography>
              </Paper>
              {discount > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Discount
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {formatAudCompact(discount)}
                  </Typography>
                </Paper>
              )}
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Paid (verified)
                </Typography>
                <Typography variant="subtitle1" fontWeight={700} color="success.main">
                  {formatAudCompact(totalPaid)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Due
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  color={balanceDue > 0 ? "warning.main" : "text.primary"}
                >
                  {formatAudCompact(balanceDue)}
                </Typography>
              </Paper>
            </Box>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                Record payment
              </Typography>
              <Box className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TextField
                  label="Amount"
                  type="number"
                  size="small"
                  required
                  value={draft.amount}
                  onChange={(e) => setDraft((prev) => ({ ...prev, amount: e.target.value }))}
                  inputProps={{ min: 0, step: "0.01" }}
                />
                <TextField
                  label="Date"
                  type="date"
                  size="small"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={draft.date}
                  onChange={(e) => setDraft((prev) => ({ ...prev, date: e.target.value }))}
                />
                <Autocomplete
                  options={PAYMENT_METHOD_OPTIONS}
                  value={PAYMENT_METHOD_OPTIONS.find((m) => m.value === draft.method) || null}
                  onChange={(_, value) =>
                    setDraft((prev) => ({ ...prev, method: value?.value || "" }))
                  }
                  getOptionLabel={(option) => option?.label || ""}
                  renderInput={(params) => (
                    <TextField {...params} label="Payment method" size="small" required />
                  )}
                />
                <TextField
                  label="Reference / receipt #"
                  size="small"
                  value={draft.reference}
                  onChange={(e) => setDraft((prev) => ({ ...prev, reference: e.target.value }))}
                />
                <TextField
                  label="Notes"
                  size="small"
                  className="md:col-span-2"
                  multiline
                  minRows={2}
                  value={draft.notes}
                  onChange={(e) => setDraft((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                <UploadFile
                  data={upload}
                  setData={setUpload}
                  imageKey="attachments"
                  label="Payment proof / attachments"
                  type="multiple"
                  newDesign
                />
              </Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 2 }}
              >
                {canSelfVerify ? (
                  <label className="flex items-center gap-2 text-sm text-app-text-secondary">
                    <input
                      type="checkbox"
                      checked={draft.autoVerify}
                      onChange={(e) =>
                        setDraft((prev) => ({ ...prev, autoVerify: e.target.checked }))
                      }
                    />
                    Add as already verified
                  </label>
                ) : (
                  <span />
                )}
                <Button
                  variant="contained"
                  onClick={submitPayment}
                  disabled={addMutation.isLoading}
                >
                  {addMutation.isLoading ? "Submitting…" : "Submit payment"}
                </Button>
              </Stack>
            </Paper>

            <Stack spacing={1.5}>
              {paymentsLoading ? (
                <Typography variant="body2" color="text.secondary" className="py-4 text-center">
                  Loading payments…
                </Typography>
              ) : payments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" className="py-4 text-center">
                  No payments recorded yet.
                </Typography>
              ) : (
                payments.map((payment) => {
                  const chip = STATUS_CHIP[payment.status] || {
                    label: payment.status,
                    color: "default",
                  };
                  const isRefund = payment.entryType === "REFUND";
                  return (
                    <Paper key={payment.id} variant="outlined" sx={{ p: 2 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}
                        flexWrap="wrap"
                      >
                        <Box className="min-w-0">
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="subtitle2" fontWeight={700}>
                              {isRefund ? "−" : ""}
                              {formatAudCompact(Number(payment.amount || 0))}
                            </Typography>
                            <Chip label={chip.label} size="small" color={chip.color} />
                            {isRefund && (
                              <Chip
                                label="Refund"
                                size="small"
                                variant="outlined"
                                color="warning"
                              />
                            )}
                            {payment.method && (
                              <Chip label={payment.method} size="small" variant="outlined" />
                            )}
                            {payment.date && (
                              <Typography variant="caption" color="text.secondary">
                                {payment.date}
                              </Typography>
                            )}
                          </Stack>
                          {payment.reference && (
                            <Typography variant="body2" color="text.secondary">
                              Ref: {payment.reference}
                            </Typography>
                          )}
                          {payment.notes && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {payment.notes}
                            </Typography>
                          )}
                          {payment.status === "PENDING_VERIFICATION" &&
                            payment.lastEditedByName && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Edited by {payment.lastEditedByName}
                              </Typography>
                            )}
                          {payment.status === "REJECTED" && payment.rejectionFeedback && (
                            <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                              Rejected: {payment.rejectionFeedback}
                            </Typography>
                          )}
                          {(payment.attachments || payment.receiptDocuments || []).length > 0 && (
                            <PaymentReceiptChips
                              urls={payment.attachments || payment.receiptDocuments || []}
                            />
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          {canChangePendingPayment(payment) && (
                            <>
                              <Tooltip title="Edit before it is verified">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setPendingState({ open: true, mode: "edit", payment })
                                  }
                                >
                                  <EditNoteOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Withdraw from verification">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    setPendingState({ open: true, mode: "withdraw", payment })
                                  }
                                >
                                  <CancelScheduleSendOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {payment.status === "REJECTED" && (
                            <Tooltip title="Resubmit for verification">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setPendingState({ open: true, mode: "resubmit", payment })
                                }
                              >
                                <ReplayOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {payment.status === "VERIFIED" && !isRefund && canModify && (
                            <Tooltip title="Modify">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setActionState({ open: true, action: "modify", payment })
                                }
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {payment.status === "VERIFIED" && !isRefund && canRefund && (
                            <Tooltip title="Refund">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setActionState({ open: true, action: "refund", payment })
                                }
                              >
                                <UndoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {payment.status === "VERIFIED" && canDelete && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  setActionState({ open: true, action: "delete", payment })
                                }
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Done</Button>
        </DialogActions>
      </Dialog>

      <PendingModulePaymentDialog
        open={pendingState.open}
        mode={pendingState.mode}
        payment={pendingState.payment}
        requireMethod
        onClose={() => setPendingState({ open: false, mode: null, payment: null })}
        onChanged={invalidate}
        onResubmit={(payment, payload) =>
          resubmitModulePayment(payment.id, payload, { cadId, isOtherEntry })
        }
        onRecordNew={({ attachments, ...values }) => {
          setDraft({ ...createEmptyDraft(canSelfVerify), ...values, method: values.method || "" });
          setUpload({ attachments: attachments || [] });
        }}
      />

      <ModulePaymentActionModal
        open={actionState.open}
        action={actionState.action}
        payment={actionState.payment}
        refundableAvailable={refundableAvailable}
        invalidateQueryKey={["cad-payment-transactions", cadId]}
        onClose={() => setActionState({ open: false, action: null, payment: null })}
        onSuccess={invalidate}
      />
    </>
  );
}
